import { dataTypeLabels as staticDataTypeLabels } from '../data/dataTypeLabels';

const staticByValue = Object.fromEntries(staticDataTypeLabels.map((d) => [d.value, d]));

let catalog = null;

export function setPricingCatalog(next) {
  catalog = next;
}

export function getPricingCatalog() {
  return catalog;
}

/** True when the API loaded pricing.yaml from S3 (dev override), not the Docker embed. */
export function isPricingConfigFromS3(catalogIn = null) {
  const c = catalogIn ?? catalog;
  if (!c) return false;
  if (c.pricing_from_s3 === true) return true;
  const src = c.config_source || '';
  return typeof src === 'string' && src.startsWith('s3://');
}

function normalizeDataTypeEntry(dt) {
  const fallback = staticByValue[dt?.value];
  const rawMult = dt?.multiplier;
  const multiplier =
    typeof rawMult === 'number' && Number.isFinite(rawMult) && rawMult > 0
      ? rawMult
      : fallback?.multiplier ?? 1;
  return {
    value: dt?.value || fallback?.value || '',
    label: dt?.label || fallback?.label || dt?.value || '',
    multiplier,
    hint: dt?.hint || fallback?.hint || '',
  };
}

/** Data-type chips: API catalog when loaded, else static fallback (tests / offline). */
export function getDataTypeLabels() {
  const types = catalog?.data_types;
  if (types?.length) {
    return types
      .map(normalizeDataTypeEntry)
      .filter((dt) => dt.value);
  }
  return staticDataTypeLabels;
}

function sizeMultiplierFromCatalog(samples) {
  const bands = catalog?.size_bands;
  if (!bands?.length) return null;
  if (!Number.isFinite(samples) || samples < 1) return 1.0;
  for (const band of bands) {
    if (samples >= band.min_samples) return band.multiplier;
  }
  return bands[bands.length - 1].multiplier;
}

function consentMultiplierFromCatalog(score) {
  const tiers = catalog?.consent_tiers;
  if (!tiers?.length) return null;
  for (const tier of tiers) {
    if (score >= tier.min_score) return tier.multiplier;
  }
  return tiers[tiers.length - 1].multiplier;
}

export function catalogSizeMultiplier(samples) {
  return sizeMultiplierFromCatalog(samples);
}

export function catalogConsentMultiplier(score) {
  return consentMultiplierFromCatalog(score);
}

export function catalogDiversityBonus(populationDiversity) {
  const f = catalog?.formula;
  if (!f) return null;
  if (!Number.isFinite(populationDiversity)) return 1.0;
  return populationDiversity >= f.diversity_threshold ? f.diversity_multiplier : 1.0;
}

export function catalogFollowUpBonus(years) {
  const f = catalog?.formula;
  if (!f) return null;
  if (!Number.isFinite(years)) return 1.0;
  if (years >= f.follow_up_years_high) return f.follow_up_multiplier_high;
  return f.follow_up_multiplier_default;
}

export function catalogDataTypeMultiplier(dataTypes) {
  const types = catalog?.data_types;
  const f = catalog?.formula;
  if (!types?.length || !f) return null;
  if (!dataTypes?.length) return 1.0;
  const multMap = Object.fromEntries(types.map((d) => [d.value, d.multiplier]));
  const mults = dataTypes.map((d) => multMap[d]).filter((m) => m && m > 0);
  if (mults.length === 0) return 1.0;
  const max = Math.max(...mults);
  const bonus = Math.min(f.data_type_bonus_per_extra * (dataTypes.length - 1), f.data_type_bonus_cap);
  return max + bonus;
}

export function catalogBaseDivisor() {
  return catalog?.formula?.base_divisor ?? null;
}
